import { Component, OnInit } from '@angular/core';
import { PolicyRepoService } from '@client/app/services/api/policy-repo/policy-repo.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { StateService } from '@uirouter/core';
import { MonUIRouterTransitionService } from '@monsido/services/ui-router-transition-service/ui-router-transition-service';
import { SessionService } from '@monsido/core/session/session.service';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { FormGlobalPolicyComponent } from '@client/app/forms/global-policy/form-global-policy.component';

@Component({
    selector: 'global-policy-dashboard',
    standalone: false,
    template: `
        <page-header [icon]="['POLICIES', 'POLICIES'] | monIcons" [header]="'Global Policies' | translate">
            <page-header-content>
                <div class="row">
                    <div class="col-48 text-right">
                        <button class="btn btn-border-radius btn-secondary"
                                data-test="add-policy-button"
                                (click)="openPolicyForm()"
                                monEventTrigger="actionTrigger"
                                [monEventParams]="{action:'before-policy-create'}"
                                translate>
                            Add new policy
                        </button>
                        <button *ngIf="showPolicyBotButton" class="btn opt-button opt-button--smaller opt-button-main"
                                (click)="goToPolicyAIBot()"
                                monEventTrigger="actionTrigger"
                                [monEventParams]="{action:'before-policy-create'}">
                                <span class="fa-lg far fa-sparkles"></span>
                                <span translate>Add Policy with AI</span>
                        </button>
                    </div>
                </div>
            </page-header-content>
        </page-header>

        <mon-spinner [progress]="progress" class="global-policy-dashboard">
            <div class="row" *ngIf="policies.length === 0">
                <div class="col-48 text-center">
                    <img [alt]="'No Policies detected' | translate" src="/images/policy/no-policies.png" class="img-responsive center-block"/>
                    <p class="pt-3 pb-6 font-size-1-5 font-weight-500" translate>No content policies have been created yet</p>
                    <button type="button" (click)="openPolicyForm()" class="btn btn-border-radius btn-marine-blue py-3 px-4" translate>
                        Click here to create your first policy
                    </button>
                </div>
            </div>
            <div class="row" *ngIf="policies.length > 0">
                <div class="col-48 col-lg-24">
                    <mon-panel-next>
                        <panel-body>
                            <div class="row">
                                <div class="col-48 col-md-24 pr-md-1 text-center">
                                    <p class="font-size-1-5" translate>Priorities</p>
                                    <p class="text-secondary-4 px-3 policy-sub-header-height" translate>
                                        Distribution of policies with matches across priority levels
                                    </p>
                                    <global-policy-priority [policies]="policies" *ngIf="policies"></global-policy-priority>
                                </div>
                                <div class="col-48 col-md-24 pl-md-1 text-center">
                                    <p class="font-size-1-5" translate>Policy Distribution</p>
                                    <p class="text-secondary-4 px-3 policy-sub-header-height" translate>
                                        Distribution of policies that match their corresponding setting
                                    </p>
                                    <global-policy-types [policies]="policies" *ngIf="policies"></global-policy-types>
                                </div>
                            </div>
                        </panel-body>
                    </mon-panel-next>
                </div>
                <div class="col-48 col-lg-24">
                    <mon-panel-next>
                        <panel-body>
                            <p class="text-center font-size-1-5 mx-3" translate>Policy Distribution</p>
                            <p class="text-center text-grey px-3 policy-sub-header-height">&nbsp;</p>
                            <global-policy-distribution [policies]="policies" *ngIf="policies"></global-policy-distribution>
                        </panel-body>
                    </mon-panel-next>
                </div>
                <div class="col-48">
                    <mon-panel-next [header]="'Policies with most matches' | translate">
                        <panel-body panel-class="inner-b-large inner-l-smurf inner-r-smurf inner-t-small">
                            <global-policy-matches
                                (getPolicies)="getPolicies()"
                                (openPolicyForm)="openPolicyForm($event.policy)"
                                *ngIf="policies">
                            </global-policy-matches>
                        </panel-body>
                    </mon-panel-next>
                </div>
            </div>
        </mon-spinner>
    `,
})
export class GlobalPolicyDashboardComponent implements OnInit {
    currentSort: any;
    policies: any[] = [];
    showPolicyBotButton: boolean = false;
    progress: any;

    constructor (
        private policyRepoService: PolicyRepoService,
        private monEventsService: MonEventService,
        private stateService: StateService,
        private monUIRouterTransitionService: MonUIRouterTransitionService,
        private sessionService: SessionService,
    ) {}

    ngOnInit () {
        this.currentSort = {
            by: 'matches_count',
            direction: 'desc',
        };
        this.policies = [];
        this.showPolicyBotButton = this.sessionService.customer?.plan_traits?.policy_bot || false;
        this.getPolicies();
    }

    openPolicyForm (policy?: any) {
        const params = {
            body: FormGlobalPolicyComponent,
            size: 'fw',
            data: {
                policy: policy,
            },
        };
        const callback = (currentPolicy: any) => {
            if (currentPolicy && typeof currentPolicy === 'object') {
                this.monUIRouterTransitionService.onTransitionFinished(() => {
                    this.stateService.go('base.customer.global.policy.policies');
                });
            }
        };
        this.monEventsService.run(MON_EVENTS.LOAD_DIALOG, { params, callback });
    }

    goToPolicyAIBot () {
        this.stateService.go('base.customer.global.policy.bot');
    }

    getPolicies () {
        const params = {
            page_size: 0,
            sort_by: this.currentSort.by,
            sort_dir: this.currentSort.direction,
            archived: false,
        };

        this.progress = this.policyRepoService.getAll(params).then((policies: any[]) => {
            this.policies = policies.sort((a: any, b: any) => {
                return a.matches_count > b.matches_count ? -1 : a.matches_count < b.matches_count ? 1 : 0;
            });
        }, () => {});
    }
}
